/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Fragment, Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { Location } from 'react-router';
import NotFoundPage from "@pages/unauthenticated/NotFound";
import AppLayout from "@layout/app";
import ManageAgencies from "@pages/authenticated/manage-agencies";
import { LoadingPage } from "@pages/other/LoadingPage";
import Loading from "@/shared/ui/components/basic/loading/Loading";
import {
  ServiceTermsRoutes,
  PrivacyPolicyRoutes,
  CookiePolicyRoutes,
  DataStorageTermsRoutes,
  AcceptableUsePolicyRoutes,
} from "@/routes/DocumentRoutes";
import { DataRightsPage } from "@features/data-rights/pages/DataRightsPage";
import { agencyContext } from "@pages/authenticated/agency-pages/AgenciesContext";
import { useLayout } from "@layout/app/LayoutContext";
import ErrorPage from "@pages/unauthenticated/ErrorPage";
import { FF_BLOG, FF_APP, FF_EARLY_ACCESS, FF_DEV_DESIGN_SYSTEM, FF_APP_WHATSAPP, FF_DEV_AGENT_DOCS } from "@/shared/utils/feature-flag";
import { DisabledFeatureRedirect } from "@/shared/components/DisabledFeatureRedirect";
import {
  DesignSystemPage,
  EarlyAccessPage,
  ZustandTest,
  AcceptInvitePage,
  Landing,
  PricingPage,
  CheckoutSuccessPage,
  UserProfile,
  UserSecurity,
  UserPreferences,
  UserHelp,
  UserWhatsApp,
  AgentCapabilitiesPage,
  AssistantPage,
} from "./App.lazy";
import UserSettingsModal from "@pages/authenticated/user-pages/UserSettingsModal";
import { useBlogRoutes, PublicShell } from "./App.shells";
import { LoggedAgencyRoutes } from "./App.agencyRoutes";

/**
 * MainRoutes component.
 *
 * @returns {JSX.Element} React component element
 */
export const MainRoutes = () => {
  const blogRoutes = useBlogRoutes();

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {FF_APP && <Route path="app/*" element={<LoggedPages />} />}

      {FF_EARLY_ACCESS && (
        <>
          <Route path="/early-access" element={<EarlyAccessPage />} />
        </>
      )}
      {FF_DEV_DESIGN_SYSTEM && (
        <Route path="/design-system/*" element={<DesignSystemPage />} />
      )}
      {FF_DEV_AGENT_DOCS && (
        <Route path="/agent-capabilities" element={<AgentCapabilitiesPage />} />
      )}
      <Route path="/pricing" element={<PricingPage />} />
      {!FF_APP && <Route path="/app/*" element={<DisabledFeatureRedirect to="/" />} />}
      {!FF_BLOG && <Route path="/blog/*" element={<DisabledFeatureRedirect />} />}
      {!FF_EARLY_ACCESS && (
        <>
          <Route path="/early-access" element={<DisabledFeatureRedirect />} />
          <Route path="/early-access/" element={<DisabledFeatureRedirect />} />
        </>
      )}
      {!FF_DEV_DESIGN_SYSTEM && <Route path="/design-system/*" element={<DisabledFeatureRedirect />} />}
      {!FF_DEV_AGENT_DOCS && <Route path="/agent-capabilities" element={<DisabledFeatureRedirect />} />}
      <Route path="/prototype/*" element={<DisabledFeatureRedirect />} />
      <Route path="/prototypes/*" element={<DisabledFeatureRedirect />} />
      <Route path="/serviceterms/*" element={<ServiceTermsRoutes />} />
      <Route path="/privacy/*" element={<PrivacyPolicyRoutes />} />
      <Route path="/cookies/*" element={<CookiePolicyRoutes />} />
      <Route path="/data-storage/*" element={<DataStorageTermsRoutes />} />
      <Route path="/acceptable-use/*" element={<AcceptableUsePolicyRoutes />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

      {FF_BLOG &&
        blogRoutes.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
      {FF_BLOG && blogRoutes.length === 0 && (
        <Route path="/blog/*" element={<LoadingPage />} />
      )}
      <Route path="/p/:agency_slug/:property_slug" element={<PublicShell />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

/**
 * LoggedPages component.
 *
 * @returns {JSX.Element} React component element
 */
export const LoggedPages = () => {
  return (
    <AppLayout>
      <LoggedRoutes />
    </AppLayout>
  );
};

const APP_BASE = "/app";

/**
 * User account panes rendered inside the settings modal. Declared once so the
 * background routes and the modal routes stay in sync.
 */
const userSettingsPanes: { segment: string; element: JSX.Element }[] = [
  { segment: "profile", element: <UserProfile /> },
  { segment: "security", element: <UserSecurity /> },
  { segment: "preferences", element: <UserPreferences /> },
  ...(FF_APP_WHATSAPP ? [{ segment: "whatsapp", element: <UserWhatsApp /> }] : []),
  { segment: "help", element: <UserHelp /> },
];

/**
 * Whether the given pathname points at one of the user-settings panes.
 *
 * @param {string} pathname - Router pathname to test.
 * @returns {boolean} True when the settings modal should be open.
 */
const isUserSettingsPath = (pathname: string): boolean =>
  userSettingsPanes.some(({ segment }) => {
    const target = `${APP_BASE}/${segment}`;
    return pathname === target || pathname.startsWith(`${target}/`);
  });

/**
 * Remembers the last non-settings app location so the settings modal renders
 * over the screen the user came from. Returns null on a direct deep link,
 * where there is no screen to keep behind the modal.
 *
 * @param {Location} location - Current router location.
 * @returns {Location | null} Location to render behind the modal.
 */
const useSettingsBackground = (location: Location): Location | null => {
  const background = useRef<Location | null>(null);

  if (!isUserSettingsPath(location.pathname)) {
    background.current = location.pathname.startsWith(`${APP_BASE}/`) ? location : null;
    return null;
  }
  return background.current;
};

/**
 * LoggedRoutes component.
 *
 * @returns {JSX.Element} React component element
 */
export const LoggedRoutes = () => {
  const { clearNavigationBar } = useLayout((state) => state);

  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/app/workspace" || location.pathname === "/app/agencies") {
      clearNavigationBar();
    }
  }, [location]);

  const { Provider: AgencyContextProvider } = agencyContext;
  const settingsBackground = useSettingsBackground(location);

  return (
    <Suspense fallback={<Loading mode="section" />}>
      <Routes location={settingsBackground ?? location}>
        <Route path="loading" element={<LoadingPage />} />
        <Route path="test-zustand" element={<ZustandTest />} />
        <Route path="privacy/data-rights" element={<DataRightsPage />} />
        <Route path="assistant" element={<AssistantPage />} />
        <Route path="workspace" element={<ManageAgencies />} />
        <Route path="workspace/acceptinvite" element={<AcceptInvitePage />} />
        {/* Links already sent by e-mail point at /app/agencies/acceptinvite.
            Kept above agencies/:agency_id so the token is not read as an
            agency id, which silently dropped the accept request. */}
        <Route path="agencies/acceptinvite" element={<AcceptInvitePage />} />
        <Route path="agencies" element={<ManageAgencies />} />
        {userSettingsPanes.map(({ segment }) => (
          <Route key={segment} path={segment} element={<Fragment />} />
        ))}
        <Route
          path="agencies/:agency_id/*"
          element={
            <AgencyContextProvider>
              <LoggedAgencyRoutes />
            </AgencyContextProvider>
          }
        />
        <Route path="*" element={<NotFoundPage redirect="/app/workspace" />} />
      </Routes>

      <Routes>
        <Route element={<UserSettingsModal />}>
          {userSettingsPanes.map(({ segment, element }) => (
            <Route key={segment} path={segment} element={element} />
          ))}
        </Route>
      </Routes>
    </Suspense>
  );
};
